import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Users, Plus, RefreshCw, Trash2, Copy, X } from 'lucide-react';
import {
  fetchAdAccounts, fetchManagedAudiences, createAudienceFromCrm, createLookalikeAudience,
  refreshAudienceSizes, deleteManagedAudience,
} from '../../redux/metaAdsSlice';
import { Button } from '../../components/ui/Button';
import alertService from '../../utils/alert';
import PageHeader from '../../components/PageHeader';
import { PageLoader } from '../../components/crm/ui';

const COUNTRIES = [
  { code: 'IN', label: 'India' }, { code: 'US', label: 'United States' }, { code: 'GB', label: 'United Kingdom' },
  { code: 'CA', label: 'Canada' }, { code: 'AU', label: 'Australia' }, { code: 'AE', label: 'United Arab Emirates' },
];

function sizeLabel(a) {
  if (a.status === 'pending') return 'Matching…';
  if (a.status === 'failed') return 'Failed';
  if (!a.approxSizeLower && !a.approxSizeUpper) return '—';
  if (a.approxSizeLower === a.approxSizeUpper) return a.approxSizeLower?.toLocaleString();
  return `${(a.approxSizeLower || 0).toLocaleString()}–${(a.approxSizeUpper || 0).toLocaleString()}`;
}

function CreateFromCrmModal({ adAccountId, onClose, onCreated }) {
  const dispatch = useDispatch();
  const { audienceActionLoading } = useSelector((s) => s.metaAds);
  const [form, setForm] = useState({ name: '', description: '', source: 'crm_leads' });

  const submit = async () => {
    if (!form.name.trim()) return alertService.error('Name Required', 'Give this audience a name first.');
    try {
      await dispatch(createAudienceFromCrm({ adAccountId, ...form })).unwrap();
      alertService.toast('success', 'Audience created — Meta is matching it now, refresh sizes in a bit.');
      onCreated();
    } catch (err) {
      alertService.error('Creation Failed', err || 'Could not create the audience.');
    }
  };

  return (
    <div className="fixed inset-0 z-[100] flex items-end sm:items-center justify-center sm:p-4">
      <div className="absolute inset-0 bg-black/40" onClick={onClose} />
      <div className="relative bg-white rounded-t-lg sm:rounded-lg shadow-[0_2px_8px_rgba(0,0,0,0.16)] w-full max-w-md max-h-[100dvh] overflow-y-auto z-10">
        <div className="px-4 sm:px-6 py-3 border-b border-[#E5E5E5] flex items-center justify-between">
          <h3 className="text-[16px] font-bold text-[#2E2E2E]">Create Audience From CRM</h3>
          <button onClick={onClose} className="text-[#5C5C5C] hover:text-[#2E2E2E]"><X size={18} /></button>
        </div>
        <div className="p-4 sm:p-6 space-y-4">
          <p className="text-[12px] text-[#5C5C5C]">
            Builds a real Meta Custom Audience directly from the emails/phone numbers already stored in this CRM —
            no manual export/upload needed. Meta matches these against its own users (contact info is sent hashed,
            never in plain text) and reports back an approximate size once matching finishes.
          </p>
          <div>
            <label className="text-[11px] font-semibold text-[#706E6B] block mb-1">Source</label>
            <select
              className="w-full h-9 px-3 bg-white border border-[#D8DDE6] rounded text-[13px] outline-none focus:border-[#0176D3] focus:ring-1 focus:ring-[#0176D3]"
              value={form.source}
              onChange={(e) => setForm({ ...form, source: e.target.value })}
            >
              <option value="crm_leads">CRM Leads</option>
              <option value="crm_contacts">CRM Contacts</option>
            </select>
          </div>
          <div>
            <label className="text-[11px] font-semibold text-[#706E6B] block mb-1">Audience Name</label>
            <input
              type="text"
              className="w-full h-9 px-3 bg-white border border-[#D8DDE6] rounded text-[13px] outline-none focus:border-[#0176D3] focus:ring-1 focus:ring-[#0176D3]"
              placeholder="e.g. All Leads — Sept 2026"
              value={form.name}
              onChange={(e) => setForm({ ...form, name: e.target.value })}
            />
          </div>
          <div>
            <label className="text-[11px] font-semibold text-[#706E6B] block mb-1">Description (optional)</label>
            <input
              type="text"
              className="w-full h-9 px-3 bg-white border border-[#D8DDE6] rounded text-[13px] outline-none focus:border-[#0176D3] focus:ring-1 focus:ring-[#0176D3]"
              value={form.description}
              onChange={(e) => setForm({ ...form, description: e.target.value })}
            />
          </div>
        </div>
        <div className="px-4 sm:px-6 py-3 border-t border-[#E5E5E5] flex justify-end gap-2">
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button onClick={submit} loading={audienceActionLoading}>Create Audience</Button>
        </div>
      </div>
    </div>
  );
}

function LookalikeModal({ origin, onClose, onCreated }) {
  const dispatch = useDispatch();
  const { audienceActionLoading } = useSelector((s) => s.metaAds);
  const [form, setForm] = useState({ name: `${origin.name} — Lookalike`, country: 'IN', ratio: 0.01 });

  const submit = async () => {
    try {
      await dispatch(createLookalikeAudience({ originAudienceId: origin._id, ...form })).unwrap();
      alertService.toast('success', 'Lookalike audience created — Meta is building it now.');
      onCreated();
    } catch (err) {
      alertService.error('Creation Failed', err || 'Could not create the lookalike audience.');
    }
  };

  return (
    <div className="fixed inset-0 z-[100] flex items-end sm:items-center justify-center sm:p-4">
      <div className="absolute inset-0 bg-black/40" onClick={onClose} />
      <div className="relative bg-white rounded-t-lg sm:rounded-lg shadow-[0_2px_8px_rgba(0,0,0,0.16)] w-full max-w-md max-h-[100dvh] overflow-y-auto z-10">
        <div className="px-4 sm:px-6 py-3 border-b border-[#E5E5E5] flex items-center justify-between">
          <h3 className="text-[16px] font-bold text-[#2E2E2E]">Create Lookalike Audience</h3>
          <button onClick={onClose} className="text-[#5C5C5C] hover:text-[#2E2E2E]"><X size={18} /></button>
        </div>
        <div className="p-4 sm:p-6 space-y-4">
          <p className="text-[12px] text-[#5C5C5C]">
            Meta finds new people who resemble <span className="font-semibold text-[#2E2E2E]">{origin.name}</span> —
            a smaller percentage is a closer match (smaller reach), a larger percentage broadens reach at the cost of similarity.
          </p>
          <div>
            <label className="text-[11px] font-semibold text-[#706E6B] block mb-1">Name</label>
            <input
              type="text"
              className="w-full h-9 px-3 bg-white border border-[#D8DDE6] rounded text-[13px] outline-none focus:border-[#0176D3] focus:ring-1 focus:ring-[#0176D3]"
              value={form.name}
              onChange={(e) => setForm({ ...form, name: e.target.value })}
            />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-[11px] font-semibold text-[#706E6B] block mb-1">Country</label>
              <select
                className="w-full h-9 px-3 bg-white border border-[#D8DDE6] rounded text-[13px] outline-none focus:border-[#0176D3] focus:ring-1 focus:ring-[#0176D3]"
                value={form.country}
                onChange={(e) => setForm({ ...form, country: e.target.value })}
              >
                {COUNTRIES.map((c) => <option key={c.code} value={c.code}>{c.label}</option>)}
              </select>
            </div>
            <div>
              <label className="text-[11px] font-semibold text-[#706E6B] block mb-1">Match Closeness</label>
              <select
                className="w-full h-9 px-3 bg-white border border-[#D8DDE6] rounded text-[13px] outline-none focus:border-[#0176D3] focus:ring-1 focus:ring-[#0176D3]"
                value={form.ratio}
                onChange={(e) => setForm({ ...form, ratio: Number(e.target.value) })}
              >
                <option value={0.01}>1% (closest match)</option>
                <option value={0.05}>5%</option>
                <option value={0.1}>10%</option>
                <option value={0.15}>15%</option>
                <option value={0.2}>20% (broadest reach)</option>
              </select>
            </div>
          </div>
        </div>
        <div className="px-4 sm:px-6 py-3 border-t border-[#E5E5E5] flex justify-end gap-2">
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button onClick={submit} loading={audienceActionLoading}>Create Lookalike</Button>
        </div>
      </div>
    </div>
  );
}

export default function MetaAdsAudiencesPage() {
  const dispatch = useDispatch();
  const { connected, adAccounts, selectedAdAccountId, managedAudiences, managedAudiencesLoading } = useSelector((s) => s.metaAds);
  const [showCreate, setShowCreate] = useState(false);
  const [lookalikeFor, setLookalikeFor] = useState(null);
  const [refreshing, setRefreshing] = useState(false);
  const [deletingId, setDeletingId] = useState(null);

  const adAccountId = selectedAdAccountId || adAccounts?.[0]?.id;

  useEffect(() => { if (connected) dispatch(fetchAdAccounts()); }, [connected, dispatch]);
  useEffect(() => { if (adAccountId) dispatch(fetchManagedAudiences(adAccountId)); }, [adAccountId, dispatch]);

  const reload = () => adAccountId && dispatch(fetchManagedAudiences(adAccountId));

  const handleRefreshSizes = async () => {
    setRefreshing(true);
    await dispatch(refreshAudienceSizes(adAccountId));
    setRefreshing(false);
  };

  const handleDelete = async (audience) => {
    const confirmed = await alertService.confirm('Delete Audience', `Delete "${audience.name}"? This removes it from Meta permanently.`, 'danger', 'Delete');
    if (!confirmed) return;
    setDeletingId(audience._id);
    await dispatch(deleteManagedAudience(audience._id));
    setDeletingId(null);
  };

  if (!connected) {
    return (
      <div className="text-center py-16 border border-dashed border-[#D8DDE6] rounded-[4px]">
        <Users size={28} className="mx-auto text-[#D8DDE6] mb-2" />
        <p className="text-[13px] text-[#5C5C5C]">Connect a Meta Ads account in Settings to build audiences.</p>
      </div>
    );
  }

  return (
    <div className="space-y-5">
      <PageHeader
        title="Audiences"
        subtitle="Custom Audiences built from your own CRM data, and Lookalikes derived from them — usable as targeting in any campaign's Audience step."
        actions={
        <div className="flex flex-wrap items-center gap-2">
          <Button variant="outline" icon={RefreshCw} onClick={handleRefreshSizes} loading={refreshing}>Refresh Sizes</Button>
          <Button onClick={() => setShowCreate(true)} icon={Plus}>Create From CRM</Button>
        </div>
        }
      />

      <div className="bg-white border border-[#E5E5E5] rounded-lg shadow-[0_1px_3px_rgba(0,0,0,0.08)] overflow-hidden">
        {managedAudiencesLoading && managedAudiences.length === 0 ? (
          <PageLoader inline />
        ) : managedAudiences.length === 0 ? (
          <div className="text-center py-16 px-6">
            <Users size={32} className="mx-auto text-[#D8DDE6] mb-3" />
            <p className="text-[13px] font-semibold text-[#2E2E2E] mb-1">No audiences yet</p>
            <p className="text-[12px] text-[#5C5C5C] mb-4">Build your first Custom Audience directly from the leads/contacts already in this CRM.</p>
            <Button onClick={() => setShowCreate(true)} icon={Plus}>Create From CRM</Button>
          </div>
        ) : (
          <div className="slds2-table-wrap"><table className="w-full text-[13px]">
            <thead className="text-[#706E6B] text-[11px] uppercase tracking-wide">
              <tr>
                <th className="text-left px-4 py-2.5">Name</th>
                <th className="text-left px-4 py-2.5">Type</th>
                <th className="text-left px-4 py-2.5">Source</th>
                <th className="text-right px-4 py-2.5">Est. Size</th>
                <th className="text-left px-4 py-2.5">Status</th>
                <th className="text-right px-4 py-2.5">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-[#D8DDE6]">
              {managedAudiences.map((a) => (
                <tr key={a._id}>
                  <td className="px-4 py-2.5 font-semibold text-[#2E2E2E]">
                    {a.name}
                    {a.description && <p className="text-[11px] font-normal text-[#5C5C5C]">{a.description}</p>}
                  </td>
                  <td className="px-4 py-2.5">
                    <span className={`sf-badge ${a.subtype === 'LOOKALIKE' ? 'sf-badge-manager' : 'sf-badge-success'}`}>{a.subtype === 'LOOKALIKE' ? 'Lookalike' : 'Custom'}</span>
                  </td>
                  <td className="px-4 py-2.5 text-[#5C5C5C]">
                    {a.sourceType === 'crm_leads' ? 'CRM Leads' : a.sourceType === 'crm_contacts' ? 'CRM Contacts' : `Lookalike of ${a.originAudienceId?.name || '—'}`}
                  </td>
                  <td className="px-4 py-2.5 text-right font-mono">{sizeLabel(a)}</td>
                  <td className="px-4 py-2.5">
                    <span className={`sf-badge ${a.status === 'ready' ? 'sf-badge-success' : a.status === 'failed' ? 'sf-badge-error' : 'sf-badge-warning'}`} title={a.error || ''}>
                      {a.status}
                    </span>
                  </td>
                  <td className="px-4 py-2.5 text-right">
                    <div className="flex items-center justify-end gap-2">
                      {a.subtype === 'CUSTOM' && a.status === 'ready' && (
                        <button title="Create Lookalike from this audience" aria-label="Create Lookalike from this audience" onClick={() => setLookalikeFor(a)} className="text-[#5C5C5C] hover:text-[#0176D3]">
                          <Copy size={14} />
                        </button>
                      )}
                      <button title="Delete" aria-label="Delete" onClick={() => handleDelete(a)} disabled={deletingId === a._id} className="text-[#5C5C5C] hover:text-[#BA0517] disabled:opacity-40">
                        <Trash2 size={14} />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table></div>
        )}
      </div>

      {showCreate && (
        <CreateFromCrmModal adAccountId={adAccountId} onClose={() => setShowCreate(false)} onCreated={() => { setShowCreate(false); reload(); }} />
      )}
      {lookalikeFor && (
        <LookalikeModal origin={lookalikeFor} onClose={() => setLookalikeFor(null)} onCreated={() => { setLookalikeFor(null); reload(); }} />
      )}
    </div>
  );
}
